import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ClipboardList } from "lucide-react";
import { MansioneSchedulingFields, ROTATIONS, WEEK } from "@/components/mansioni/MansioneSchedulingFields";

const PRESET_COLORS = ["#556B2F", "#1C352D", "#E2725B", "#5B7FE2", "#7C3AED", "#B45309", "#DB2777", "#0D9488"];
const EMPTY = { name: "", description: "", color: "#556B2F", active: true, resources: 1, days: [0, 1, 2, 3, 4, 5, 6], start_time: "", end_time: "", rotation: "weekly", excluded_student_ids: [], time_slots: [{ start_time: "", end_time: "" }], all_students: false, gender: "" };
const slotsOf = (m) => (m.time_slots?.length ? m.time_slots : m.start_time ? [{ start_time: m.start_time, end_time: m.end_time }] : []);
const toPayload = (f) => {
  const ts = f.time_slots.filter((s) => s.start_time || s.end_time).map((s) => ({ start_time: s.start_time || null, end_time: s.end_time || null }));
  return { ...f, time_slots: ts, start_time: ts[0]?.start_time || null, end_time: ts[0]?.end_time || null };
};
const daysLabel = (days) => (!days || days.length === 7 ? "Tutti i giorni" : WEEK.filter(([d]) => days.includes(d)).map(([, l]) => l).join(" "));

export default function MansioniCatalogo() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: mansioni = [] } = useQuery({
    queryKey: ["mansioni-all"],
    queryFn: async () => (await api.get("/mansioni-area/mansioni")).data,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students-list", "attivo", "resid"],
    queryFn: async () => (await api.get("/students", { params: { status: "attivo", track_type: "residenziale" } })).data,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      if (editing) return (await api.patch(`/mansioni-area/mansioni/${editing.id}`, { ...toPayload(form), gender: form.gender || "" })).data;
      return (await api.post("/mansioni-area/mansioni", { ...toPayload(form), gender: form.gender || null })).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Mansione aggiornata" : "Mansione creata");
      qc.invalidateQueries({ queryKey: ["mansioni-all"] });
      setOpen(false); setEditing(null);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const importMut = useMutation({
    mutationFn: async () => (await api.post("/mansioni-area/generator/import-rdm")).data,
    onSuccess: (r) => { toast.success(r.created ? `${r.created} mansioni importate` : "Tutte le mansioni del mansionario sono già presenti"); qc.invalidateQueries({ queryKey: ["mansioni-all"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mansioni-area/mansioni/${id}`)).data,
    onSuccess: () => { toast.success("Mansione eliminata"); qc.invalidateQueries({ queryKey: ["mansioni-all"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const openEdit = (m) => {
    setEditing(m);
    const ts = slotsOf(m);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(m).filter(([k, v]) => k in EMPTY && v !== null && v !== undefined)),
      time_slots: ts.length ? ts.map((s) => ({ start_time: s.start_time || "", end_time: s.end_time || "" })) : EMPTY.time_slots });
    setOpen(true);
  };
  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Catalogo Mansioni"
        subtitle="Tipologie di attività assegnabili ai turni (Cucina, Pulizia, Portineria...)."
        actions={
          <>
          <Button variant="outline" className="rounded-full" disabled={importMut.isPending} onClick={() => confirm("Importare le 10 mansioni del Mansionario Pulizie RdM? Quelle già presenti con lo stesso nome vengono saltate.") && importMut.mutate()} data-testid="mansioni-import-rdm">
            <ClipboardList size={14} className="mr-1.5"/> Importa mansionario RdM
          </Button>
          <Button data-testid="mansioni-add-btn" onClick={openNew} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
            <Plus size={14} className="mr-1.5"/> Nuova mansione
          </Button>
          </>
        }
      />
      <Card className="border border-stone-200 rounded-xl shadow-none">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
              <TableHead className="uppercase text-[10px] tracking-widest w-8">Colore</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Nome</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Descrizione</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Pianificazione</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Stato</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mansioni.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-stone-400">Nessuna mansione. Creane una per iniziare.</TableCell></TableRow>}
            {mansioni.map((m) => (
              <TableRow key={m.id}>
                <TableCell><div className="w-4 h-4 rounded-full" style={{ background: m.color }}/></TableCell>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="text-sm text-stone-500 max-w-[320px]"><div className="line-clamp-2 whitespace-pre-line">{m.description || "—"}</div></TableCell>
                <TableCell className="text-xs text-stone-600" data-testid={`mansione-plan-${m.id}`}>
                  <div>{m.all_students ? <b>Tutti</b> : <><b>{m.resources || 1}</b> {(m.resources || 1) === 1 ? "persona" : "persone"}</>} · {daysLabel(m.days)}{slotsOf(m).map((s) => ` · ${s.start_time || ""}${s.end_time ? `–${s.end_time}` : ""}`).join("")}</div>
                  <div className="text-stone-400">{ROTATIONS[m.rotation || "weekly"]}{m.gender ? ` · solo ${m.gender === "M" ? "uomini" : "donne"}` : ""}{m.excluded_student_ids?.length ? ` · ${m.excluded_student_ids.length} esclusi` : ""}</div>
                </TableCell>
                <TableCell>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.active ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-stone-100 text-stone-500"}`}>
                    {m.active ? "Attiva" : "Disattivata"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(m)} data-testid={`mansione-edit-${m.id}`}>
                    <Pencil size={13}/>
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => { if (confirm("Eliminare la mansione? (fallirà se ci sono turni collegati)")) delMut.mutate(m.id); }} data-testid={`mansione-del-${m.id}`}>
                    <Trash2 size={13}/>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica mansione" : "Nuova mansione"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input data-testid="mansione-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1.5" placeholder="Es. Cucina, Pulizia, Portineria"/>
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1.5" rows={5} placeholder={"Cosa deve fare lo studente, es.\n1. Apparecchiare\n2. Lavare le pentole"}/>
            </div>
            <div>
              <Label>Colore</Label>
              <div className="flex gap-2 mt-1.5 flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setForm({...form, color: c})} className={`w-8 h-8 rounded-full border-2 transition ${form.color === c ? "border-stone-900 scale-110" : "border-white"}`} style={{ background: c }} data-testid={`color-${c}`}/>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} data-testid="mansione-active"/>
              <span>Attiva (visibile nei form turni)</span>
            </label>
            <MansioneSchedulingFields form={form} setForm={setForm} students={students}/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="mansione-save" className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={!form.name || !form.days.length || saveMut.isPending} onClick={() => saveMut.mutate()}>
              {saveMut.isPending ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
